import { useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/** Modal para ver una imagen en grande.
 *  Props:
 *    - src (url o data URI)
 *    - alt (leyenda debajo de la imagen)
 *    - open, onOpenChange
 *    - onPrev, onNext (opcionales) → habilita flechas y navegación por teclado
 *    - position, total (opcionales) → muestra "1 / 8" arriba
 */
export default function ImageLightbox({ src, alt, open, onOpenChange, onPrev = null, onNext = null, position = null, total = null }) {
  // Navegación por teclado: ← → cambian, Esc cierra
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "ArrowLeft" && onPrev) { e.preventDefault(); onPrev(); }
      else if (e.key === "ArrowRight" && onNext) { e.preventDefault(); onNext(); }
      else if (e.key === "Escape") { e.preventDefault(); onOpenChange(false); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onPrev, onNext, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-2 bg-transparent border-0 shadow-none" data-testid="image-lightbox">
        <div className="relative">
          <button
            onClick={() => onOpenChange(false)}
            className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-card border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground z-10"
            data-testid="lightbox-close"
            title="Cerrar (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
          {onPrev && (
            <button
              onClick={onPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-card/90 border border-border shadow-md flex items-center justify-center text-foreground hover:bg-card z-10"
              data-testid="lightbox-prev"
              title="Anterior (←)"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {onNext && (
            <button
              onClick={onNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-card/90 border border-border shadow-md flex items-center justify-center text-foreground hover:bg-card z-10"
              data-testid="lightbox-next"
              title="Siguiente (→)"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          {position !== null && total !== null && total > 1 && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-foreground/70 text-white text-xs px-2.5 py-1 rounded-full font-semibold z-10 tabular-nums">
              {position} / {total}
            </div>
          )}
          {src ? (
            <img
              src={src}
              alt={alt || "Producto"}
              className="w-full max-h-[85vh] object-contain rounded-lg bg-card shadow-2xl"
              data-testid="lightbox-image"
            />
          ) : null}
          {alt && (
            <div className="mt-2 text-center text-sm text-foreground bg-card/90 backdrop-blur px-4 py-2 rounded-lg">
              {alt}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
